import { validateDetailViews, validateDetailContent } from '@/ai/protocol/detail-views'
// AI V1纯数据协议校验；未知必需组件阻止交互，不执行脚本或HTML。
import { COMPONENT_VERSIONS, componentSupport } from '@/ai/protocol/component-support'
export const CAPABILITIES = Object.freeze({ components: COMPONENT_VERSIONS, inputs: Object.freeze({}), features: Object.freeze({ action_execute: 1 }) })
export const STATES = [
  'QUEUED',
  'GENERATING',
  'WAITING_ACTION',
  'COMPLETED',
  'FAILED',
  'UNKNOWN',
  'CANCELLED',
  'EXPIRED'
]
export const RESULTS = [
  'NOT_STARTED',
  'DISPATCHING',
  'ACCEPTED',
  'SUCCEEDED',
  'FAILED',
  'UNKNOWN',
  'CANCELLED',
  'EXPIRED'
]
export const TERMINAL = [
  'COMPLETED',
  'FAILED',
  'UNKNOWN',
  'CANCELLED',
  'EXPIRED'
]
export function invariant(ok, code = 'INVALID_REQUEST') {
  if (!ok) throw new Error(code)
}
export function id(value) {
  invariant(typeof value === 'string' && /^[0-9]{1,20}$/.test(value))
}
export function text(value, max = 2000) {
  invariant(
    typeof value === 'string' && value.length > 0 && [...value].length <= max
  )
}
export function version(value) {
  invariant(Number.isSafeInteger(value) && value >= 0)
}
export function validateSnapshot(s, caps = CAPABILITIES) {
  invariant(
    s.protocolMajor === 1 &&
      Number.isSafeInteger(s.protocolMinor) &&
      s.protocolMinor >= 0,
    'CLIENT_UNSUPPORTED'
  )
  id(s.sessionId)
  id(s.turnId)
  version(s.revision)
  invariant(STATES.includes(s.state))
  invariant(Number.isSafeInteger(s.turnNo) && s.turnNo > 0)
  invariant(Number.isSafeInteger(s.pollAfterMs) && s.pollAfterMs >= 0)
  invariant(Array.isArray(s.components) && s.components.length <= 20)
  invariant(
    new TextEncoder().encode(JSON.stringify(s)).length <= 65536,
    'PAYLOAD_TOO_LARGE'
  )
  const ids = new Set()
  let blocked = s.readOnly === true
  for (const c of s.components) {
    text(c.id, 64)
    invariant(!ids.has(c.id))
    ids.add(c.id)
    invariant(typeof c.required === 'boolean')
    if (componentSupport(c, caps.components) === 'unsupported') {
      if (c.required) blocked = true
      continue
    }
    if (c.type === 'scene_cards') { validateDetailContent(c, true); if (c.hasMore && typeof c.nextCursor !== 'string') throw new Error('INVALID_SCENE_CURSOR') }
    if (c.type === 'detail_views') validateDetailViews(c)
    if (c.type === 'text') text(c.text)
    if (c.type === 'action_list') {
      invariant(c.executionMode === 'explicit')
      invariant(
        Array.isArray(c.items) &&
          c.items.length <= (c.executionMode === 'explicit' ? 60 : 20) &&
          typeof c.hasMore === 'boolean' &&
          typeof c.truncated === 'boolean'
      )
      invariant(
        c.hasMore ? typeof c.nextCursor === 'string' : c.nextCursor === null
      )
      if (c.executionMode != null) {
        invariant(c.executionMode === 'explicit')
        invariant(c.actionKind == null || ['execute', 'confirm', 'select'].includes(c.actionKind))
        if (c.actionKind && c.actionKind !== 'execute') invariant(c.maxSelection === 1)
        if (c.actionKind === 'confirm') invariant(c.items.length === 1 && c.required === true)
        invariant(Number.isInteger(c.maxSelection) && c.maxSelection >= 1 && c.maxSelection <= 20)
        for (const item of c.items) {
          invariant(['device','scene'].includes(item.objectKind) && typeof item.available === 'boolean')
          text(item.operationText, 300)
          if (item.deviceTypeName != null) text(item.deviceTypeName, 100)
          if (item.objectKind === 'scene') invariant(c.maxSelection === 1)
          if (item.result) { invariant(['QUEUED','EXECUTING','SUCCEEDED','ACCEPTED','FAILED','UNKNOWN'].includes(item.result.state)); text(item.result.text, 300) }
        }
      }
      const refs = new Set()
      for (const item of c.items) {
        text(item.itemRef, 128)
        text(item.title, 100)
        if (item.statusText != null) text(item.statusText, 100)
        invariant(!refs.has(item.itemRef))
        refs.add(item.itemRef)
      }
    }
    if (c.type === 'device_cards') {
      text(c.title, 100)
      invariant(Array.isArray(c.items) && c.items.length <= 100)
      invariant(typeof c.truncated === 'boolean')
      if (c.hasMore !== undefined) {
        invariant(typeof c.hasMore === 'boolean')
        invariant(c.hasMore ? typeof c.nextCursor === 'string' : c.nextCursor === null)
      }
      for (const item of c.items) {
        text(item.title, 100); if (item.subtitle !== undefined && item.subtitle !== null && item.subtitle !== "") text(item.subtitle, 100)
        invariant(item.ref && typeof item.ref === 'object')
        text(item.ref.regionId, 20); text(item.ref.productId, 20); text(item.ref.title, 100)
        if (item.ref.deviceId != null) text(item.ref.deviceId, 20)
        if (item.preview != null) {
          invariant(typeof item.preview === 'object' && !Array.isArray(item.preview))
          invariant(item.preview.regionId === item.ref.regionId && item.preview.productId === item.ref.productId)
          if (item.ref.deviceId) invariant(item.preview.deviceId === item.ref.deviceId)
          text(item.preview.productCode, 20)
        }
      }
    }
    if (c.type === 'info_list') {
      text(c.title, 100)
      invariant(['rooms','scenes','statistics'].includes(c.kind))
      invariant(Array.isArray(c.items) && c.items.length <= 1000)
      const ids = new Set()
      for (const item of c.items) { text(item.id, 150); invariant(!ids.has(item.id)); ids.add(item.id); text(item.label, 100); text(item.value, 200) }
      invariant(typeof c.hasMore === 'boolean')
      if (c.hasMore) text(c.nextCursor, 300)
      invariant(!c.interaction)
    }
    if (c.type === 'task') {
      text(c.title, 100)
      if (c.progressCode !== undefined) text(c.progressCode, 40)
      invariant(RESULTS.includes(c.executionStatus))
    }
    if (c.interaction) {
      invariant(s.readOnly !== true)
      const a = c.interaction
      id(a.actionId)
      text(a.actionToken, 256)
      version(a.actionRevision)
      invariant(!Number.isNaN(Date.parse(a.expiresAt)))
      const expected = {
        action_list: [c.actionKind || (c.executionMode === 'explicit' ? 'execute' : 'select'), 'cancel'],
      }[c.type]
      invariant(
        expected &&
          a.allowedKinds.length === expected.length &&
          expected.every(k => a.allowedKinds.includes(k))
      )
      invariant(s.state === 'WAITING_ACTION')
    }
    if (
      !s.readOnly &&
      s.state === 'WAITING_ACTION' &&
      c.type === 'action_list'
    )
      invariant(c.required && c.interaction)
  }
  return { blocked }
}
export function acceptSnapshot(current, incoming, caps = CAPABILITIES) {
  const result = validateSnapshot(incoming, caps)
  if (current) {
    invariant(
      current.sessionId === incoming.sessionId &&
        current.turnId === incoming.turnId,
      'WRONG_RESPONSE'
    )
    if (incoming.revision <= current.revision)
      return {
        snapshot: current,
        blocked: validateSnapshot(current, caps).blocked
      }
  }
  return { snapshot: incoming, blocked: result.blocked }
}
